import { useState } from "react";
import { MapPin, X } from "lucide-react";

import EtaPicker from "./EtaPicker";
import { clockTime, timeLeft } from "../../utils/format";
import type { OnMyWay } from "../../data/types";
import "../../Styles/Presence.css";

/**
 * Din pågående resa.
 *
 * Visar vart, när och i vilket läge. "I'm here" är den enda knappen som
 * betyder något för loopen — den skickar ankomsten, och backend svarar med
 * check-in:en den skapade.
 *
 * Alla knappar är låsta medan ett anrop pågår. Ett dubbeltryck ska aldrig
 * kunna ge två resor eller två check-ins; servern är idempotent, men UI:t
 * ska inte ens be den om det.
 */
type Props = {
  onMyWay: OnMyWay;
  venueName: string;
  busy: boolean;
  error?: string | null;
  onChangeEta: (minutes: number) => void;
  onCancel: () => void;
  onArrive: () => void;
};

export default function OnMyWayCard({
  onMyWay,
  venueName,
  busy,
  error,
  onChangeEta,
  onCancel,
  onArrive,
}: Props) {
  const [isChangingEta, setIsChangingEta] = useState(false);

  const remaining = timeLeft(onMyWay.etaAt);

  return (
    <div className="omw-card" data-testid="on-my-way-card">
      <div className="omw-card-head">
        <MapPin size={14} strokeWidth={2.2} />
        <div className="omw-card-text">
          <strong>On the way to {venueName}</strong>
          <small>
            {/* Passerad ETA betyder inte att resan är slut — den lever
                vidare en stund till, se ONMYWAY_GRACE_MINUTES. */}
            {remaining
              ? `Arriving around ${clockTime(onMyWay.etaAt)} · ${remaining} left`
              : `Expected around ${clockTime(onMyWay.etaAt)}`}
          </small>
        </div>

        <span className="omw-status">{onMyWay.status.replace("_", " ")}</span>
      </div>

      {error && <p className="presence-error">{error}</p>}

      {isChangingEta ? (
        <EtaPicker
          label="New arrival time"
          disabled={busy}
          onPick={(minutes) => {
            setIsChangingEta(false);
            onChangeEta(minutes);
          }}
        />
      ) : (
        <div className="omw-card-actions">
          <button
            type="button"
            className="presence-primary"
            disabled={busy}
            onClick={onArrive}
          >
            {busy ? "…" : "I'm here"}
          </button>

          <button
            type="button"
            className="presence-ghost"
            disabled={busy}
            onClick={() => setIsChangingEta(true)}
          >
            Change ETA
          </button>

          <button
            type="button"
            className="presence-ghost"
            disabled={busy}
            onClick={onCancel}
          >
            <X size={13} strokeWidth={2.4} />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
